import { yupResolver } from "@hookform/resolvers/yup"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import * as Yup from "yup"

import withGiftBundles from "areas/cart/hoc/with-gift-bundles"
import Button from "components/ui/button"
import Checkbox from "components/ui/checkbox"
import Image from "components/ui/image"
import Textarea from "components/ui/textarea"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"

import { CardItem } from "../components"

const MAX_CHARS = 200
const MAX_LINES = 7

const validateMsg = (message) => {
  const rows = message.split("\n")
  const newRows = rows.length > MAX_LINES ? rows.slice(0, MAX_LINES) : rows
  return newRows.join("\n")
}

const handleChange = (e, setValue) => {
  const text = e.target.value
  const keycode = e.keycode || e.which || e.charCode
  const rows = text.split("\n")
  if (
    (rows.length >= MAX_LINES && keycode === 13) ||
    (text.length >= MAX_CHARS && ![8, 46].includes(keycode))
  ) {
    e.preventDefault()
  }
  setValue("message", validateMsg(text))
}

const handleDrop = (e, setValue) => {
  e.preventDefault()
  const text = e.dataTransfer.getData("text")
  setValue("message", validateMsg(text))
}

const schema = Yup.object().shape({
  message: Yup.string().max(200, "Maximum 200 characters"),
})

const VIEW = {
  ADD: "ADD",
  EDIT: "EDIT",
  HIDE: "HIDE",
}

const CustomMessage = ({
  handleGiftBundle,
  selectedGiftBundleOption,
  selectedMessage,
  view,
  setView,
}) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      message: selectedMessage || "",
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const message = watch("message")
  const charsLeft = MAX_CHARS - (message?.length || 0)

  const onSubmit = async (data) => {
    try {
      await new Promise((res, rej) => {
        handleGiftBundle(
          {
            ...selectedGiftBundleOption,
            ...data,
          },
          {
            onError: (err) => rej(err),
            onSuccess: () => {
              setView(VIEW.HIDE)
              res()
            },
          }
        )
      })
    } catch (err) {
      setError("root.error", { message: err })
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ "--tablet-width": "150px" }}
    >
      <Controller
        control={control}
        name="message"
        render={({ field, fieldState: { error } }) => (
          <>
            <Textarea
              className="border-grey-300 rounded-sm px-12 py-8"
              data-cy="cart-line-item-greeting-card-message"
              error={error}
              onDrop={(e) => handleDrop(e, setValue)}
              onKeyDown={(e) => handleChange(e, setValue)}
              onKeyUp={(e) => handleChange(e, setValue)}
              onPaste={(e) => handleChange(e, setValue)}
              placeholder="Custom message here"
              rows={6}
              {...field}
            />
            <p className="text-grey-500 text-12 mt-4">
              {MAX_CHARS} Characters Max / {charsLeft}{" "}
              {`Character${charsLeft > 1 ? "s" : ""}`} Left
            </p>
            {errors?.root?.error && (
              <p className="text-error my-8">{errors.root.error.message}</p>
            )}
            {view === VIEW.ADD && (
              <Button
                aria-label="Add custom message"
                className="text-18 tablet:w-auto tablet:min-w-(--tablet-width) mt-16 w-full font-semibold"
                disabled={!!error || isSubmitting}
                loading={isSubmitting}
                type="submit"
                variant="primary"
              >
                Add
              </Button>
            )}
            {view === VIEW.EDIT && (
              <div className="tablet:block mt-16 flex gap-12">
                <Button
                  aria-label="Cancel message"
                  className="text-18 tablet:w-auto tablet:min-w-(--tablet-width) basis-1/2 border font-semibold"
                  disabled={isSubmitting}
                  onClick={() => {
                    setView(VIEW.HIDE)
                  }}
                  variant="tertiary"
                >
                  Cancel
                </Button>
                <Button
                  aria-label="Save message"
                  className="text-18 tablet:w-auto tablet:min-w-(--tablet-width) tablet:ml-16 basis-1/2 font-semibold"
                  disabled={!!error || isSubmitting}
                  loading={isSubmitting}
                  type="submit"
                  variant="primary"
                >
                  Save message
                </Button>
              </div>
            )}
          </>
        )}
      />
    </form>
  )
}

CustomMessage.propTypes = {
  handleGiftBundle: PropTypes.func,
  selectedGiftBundleOption: PropTypes.object,
  selectedMessage: PropTypes.string,
  setView: PropTypes.func,
  view: PropTypes.string,
}

const GiftBundleItem = ({
  giftBundle,
  handleClick,
  preview,
  selected,
  styleCode,
  tabIndex,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <CardItem
      aria-checked={selected}
      aria-label={giftBundle.title}
      className="tablet:basis-(--tablet-basis)"
      key={`item-bundle-${giftBundle.addon_code}`}
      onClick={handleClick}
      role="radio"
      selected={selected}
      tabIndex={tabIndex}
      {...keyboardProps}
    >
      <Image
        alt=""
        data-cy={`cart-line-item-gift-wrap-${giftBundle.addon_code}-${styleCode}`}
        height="164"
        src={giftBundle.image_url}
        style={{ width: "90px" }}
        width="180"
      />

      <p className="text-12 tracking-tigher leading-14 text-grey-55 font-medium">
        {giftBundle.title}
      </p>
      <p
        className="text-primary text-12 tracking-tigher font-medium underline"
        style={{ height: "14px" }}
      >
        {preview && "Preview"}
      </p>
    </CardItem>
  )
}

GiftBundleItem.propTypes = {
  giftBundle: PropTypes.object,
  handleClick: PropTypes.func,
  preview: PropTypes.bool,
  selected: PropTypes.bool,
  styleCode: PropTypes.string,
  tabIndex: PropTypes.number,
}

const GiftBundles = ({ handleGiftBundle, item, selectedGiftBundle }) => {
  const [view, setView] = useState(VIEW.ADD)

  const giftBundleOptions = item.gift_bundle_options || []

  const selectedAddonCode = selectedGiftBundle?.addon_code
  const selectedMessage =
    selectedGiftBundle?.custom_properties?.greeting_card_properties?.message

  const selectedGiftBundleOption = giftBundleOptions.find(
    ({ addon_code }) => addon_code === selectedAddonCode
  )

  if (!giftBundleOptions.length) {
    return null
  }

  return (
    <>
      <Checkbox
        checked={!!selectedGiftBundle}
        className="gap-10"
        data-cy={`cart-line-item-gift-wrap-checkbox-${item.style_code}`}
        onChange={() => {
          handleGiftBundle(selectedGiftBundle ? null : giftBundleOptions[0])
        }}
      >
        Add Gift Packaging (
        {giftBundleOptions[0].price > 0
          ? `+${to_currency(giftBundleOptions[0].price)}`
          : "FREE"}
        )
      </Checkbox>
      <div
        aria-labelledby={`cart-line-item-gift-bundle-group-${item.id}`}
        role="radiogroup"
      >
        <h5
          className="ml-26 text-12 tracking-tigher leading-12 mb-16 mt-12 font-medium"
          id={`cart-line-item-gift-bundle-group-${item.id}`}
        >
          Select a gifting option of your choice.
        </h5>
        <FocusScope restoreFocus>
          <div
            className="mb-16 flex justify-start overflow-hidden pb-10"
            data-cy={`cart-line-item-gift-wrap-${item.style_code}`}
            style={{
              "--tablet-basis": "135px",
            }}
          >
            {giftBundleOptions.map((giftBundle, i) => {
              const selected = giftBundle.addon_code === selectedAddonCode

              return (
                <GiftBundleItem
                  giftBundle={giftBundle}
                  handleClick={() => {
                    if (!selected) {
                      handleGiftBundle(giftBundle)
                    }
                    if (view === VIEW.HIDE) {
                      setView(selected ? VIEW.EDIT : VIEW.ADD)
                    } else if (view === VIEW.EDIT && !selected) {
                      setView(VIEW.ADD)
                    }
                  }}
                  key={`item-bundle-${giftBundle.addon_code}`}
                  preview={selected && !!selectedMessage}
                  selected={selected}
                  styleCode={item.style_code}
                  tabIndex={
                    selected || (!selectedGiftBundle && i === 0) ? 0 : -1
                  }
                />
              )
            })}
          </div>
          {selectedGiftBundleOption &&
            selectedGiftBundleOption.can_add_message &&
            view !== VIEW.HIDE && (
              <CustomMessage
                handleGiftBundle={handleGiftBundle}
                selectedGiftBundleOption={selectedGiftBundleOption}
                selectedMessage={selectedMessage}
                setView={setView}
                view={view}
              />
            )}
        </FocusScope>
      </div>
    </>
  )
}

GiftBundles.propTypes = {
  handleGiftBundle: PropTypes.func,
  item: PropTypes.object,
  selectedGiftBundle: PropTypes.object,
}

export default withGiftBundles(GiftBundles)
